import type { NextPage } from 'next'
import React from 'react'

import { ApiHandler } from '@hozana/api/ApiHandler'
import { useApiData } from '@hozana/api/hooks/useApiData'
import { createGetServerSideProps } from '@hozana/api/server/serverSide'
import { useRouter } from '@hozana/router'

import { BorderedDiv } from 'elements/layout/BorderedDiv'
import { Container } from 'elements/layout/Container'
import { Link } from 'elements/text/Link'
import { P } from 'elements/text/P'
import { PageTitle } from 'elements/text/PageTitle'

import { Page } from 'general/structure/Page'
import { NAMESPACE } from 'i18n/constants'
import { PAGE } from 'routes/constants'

import { CommunityAuthorizationsCheck } from 'modules/community/components/CommunityAuthorizationsCheck'
import { CommunityThumbnail } from 'modules/community/components/CommunityThumbnail'
import { COMMUNITY_AUTHORIZATIONS } from 'modules/community/constants'
import { PublicationForm } from 'modules/publication/components/PublicationForm'
import { usePublication } from 'modules/publication/hooks'
import { getPublicationQuery } from 'modules/publication/queries'

export const getServerSideProps = createGetServerSideProps(
  PAGE.PUBLICATION_EDIT,
  'publication:publication.edit.edit',
  undefined,
  {
    namespaces: [NAMESPACE.PUBLICATION, NAMESPACE.COMMUNITY, NAMESPACE.UPLOAD],
  },
)

export const PublicationEditPage: NextPage = () => {
  const { query } = useRouter()
  const publicationId = Number(query.publicationId)
  const [, publicationQuery] = useApiData(
    () => ({
      query: getPublicationQuery({ publicationId, withRawContent: true }),
      disabled: !publicationId,
    }),
    [publicationId],
  )
  const publication = usePublication(publicationId)

  return (
    <Page>
      <Container maxW="740px" p={{ xs: '5px 5px 100px', sm: '20px 10px 100px' }}>
        {publication && (
          <BorderedDiv m="10px auto 30px" p="8px" borderRadius="20px" borderColor="black10">
            <CommunityThumbnail communityId={publication.community} />
          </BorderedDiv>
        )}
        <PageTitle align="center" color="darkBlue" m="0 0 10px" />
        <Link
          to={{
            pathname: PAGE.STATIC,
            query: { pageSlug: 'publication-advice' },
          }}
          target="_blank"
          color="blue"
          hoverColor="darkBlue"
        >
          <P firstUpper bold align="center" m="10px 0 20px">
            trans:publication:publication.successful-tips
          </P>
        </Link>
        <ApiHandler queries={[publicationQuery]}>
          {publication && (
            <CommunityAuthorizationsCheck
              requestedAuthorization={COMMUNITY_AUTHORIZATIONS.CAN_EDIT_PUBLICATIONS}
              communityId={publication.communityId}
            >
              <PublicationForm publication={publication} />
            </CommunityAuthorizationsCheck>
          )}
        </ApiHandler>
      </Container>
    </Page>
  )
}
